
<template>
  <h2>
    <span>{{ months[currentMonth] }} {{ currentYear }}</span>
    <button
      @click="generatePDF"
      class="button-save-outline"
      :disabled="!hasTicketsForSelected"
      title="Generuj PDF"
    >
      {{ t('generate-pdf') }}
    </button>
  </h2>
  
  <!-- Tabela z ticketami (zgłoszeniami awarii) -->
  <table class="tickets-table">
    <thead>
      <tr>
        <th>{{ t('ordinal-number') }}</th>
        <th>{{ t('failure-description') }}</th>
        <th>{{ t('failure-report-date') }}</th>
        <th>{{ t('failure-removal-report-date') }}</th>
        <th>{{ t('failure-duration') }}</th>
        <th>{{ t('total-duration') }}</th>
        <th>Akcja</th>
      </tr>
    </thead>

    <!-- Dane -->
    <tbody v-if="hasTicketsForSelected">
      <tr
        v-for="(ticket, index) in paginatedTickets"
        :key="ticket.id"
        class="clickable-row"
        @click="goToDetails(ticket.id)"
        :title="`Zobacz szczegóły zgłoszenia #${ticket.id}`"
      >
        <td>{{ index + 1 }}</td>
        <td>{{ ticket.title }}</td>
        <td>{{ formatDate(ticket.created_at) }}</td>
        <td>{{ formatDate(ticket.closed_at) || t('not-completed') }}</td>
        <td>{{ calculateDowntime(ticket.created_at, ticket.closed_at) }}</td>
        <td>{{ totalDowntime(paginatedTickets) }}</td>
        <td>
          <button @click.stop="goToEdit(ticket.id)" class="btn button-edit">{{t('edit')}}</button>
        </td>
      </tr>
    </tbody>

    <!-- Pusty stan -->
    <tbody v-else>
      <tr>
        <td :colspan="7" class="empty-cell">
          {{ 'Brak zgłoszeń' || t('no-reports-for-period') }}
        </td>
      </tr>
    </tbody>
  </table>

  <!-- Sekcja wyboru daty: wybór roku i miesiąca -->
  <div class="date-picker">
    <div class="year-selector">
      <button
        v-for="year in availableYears"
        :key="year"
        :class="{ active: year === currentYear }"
        @click="selectYear(year)"
      >
        {{ year }}
      </button>
    </div>
    <div class="month-selector">
      <button
        v-for="month in availableMonths"
        :key="month.index"
        :class="{ active: month.index === currentMonth }"
        @click="selectMonth(month.index)"
      >
        {{ month.name }}
      </button>
    </div>
  </div>
</template>

<script>
import TicketsService from '../services/TicketsService.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default {
  name: "TicketsTable",
  data() {
    return {
      tickets: [], // wszystkie zgłoszenia
      currentMonth: null, // aktualnie wybrany miesiąc
      currentYear: null,  // aktualnie wybrany rok
      months: [
        "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
        "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
      ]
    };
  },
  mounted() {
    this.fetchTickets(); // pobiera tickety po zamontowaniu komponentu
  },
  computed: {
    // Zwraca tylko tickety z wybranego miesiąca i roku
    paginatedTickets() {
      return this.tickets.filter(ticket => {
        const createdAt = new Date(ticket.created_at);
        return createdAt.getMonth() === this.currentMonth && createdAt.getFullYear() === this.currentYear;
      });
    },
    hasAnyTickets() {
      return this.tickets.length > 0;
    },
    hasTicketsForSelected() {
      return this.paginatedTickets.length > 0;
    },
    headerLabel() {
      return this.currentMonth !== null && this.currentYear !== null
        ? `${this.months[this.currentMonth]} ${this.currentYear}`
        : this.$t?.('reports') || 'Zgłoszenia';
    },
    // Lista dostępnych lat z ticketa
    availableYears() {
      const years = this.tickets.map(ticket => new Date(ticket.created_at).getFullYear());
      return [...new Set(years)].sort((a, b) => a - b);
    },
    // Lista dostępnych miesięcy dla wybranego roku
    availableMonths() {
      const monthsWithTickets = this.tickets
        .filter(ticket => new Date(ticket.created_at).getFullYear() === this.currentYear)
        .map(ticket => new Date(ticket.created_at).getMonth());

      if (monthsWithTickets.length === 0) return [];

      const minMonth = Math.min(...monthsWithTickets);
      const maxMonth = Math.max(...monthsWithTickets);

      return this.months
        .map((name, index) => ({ name, index }))
        .filter(month => month.index >= minMonth && month.index <= maxMonth);
    }
  },
  methods: {
    // Pobiera tickety przez serwis i ustawia domyślnie najnowszy rok/miesiąc
    async fetchTickets() {
        TicketsService.getTickets()
        .then((response) => {
            this.tickets = response;

            if (this.tickets.length > 0) {
                const latestTicket = this.tickets.reduce((latest, ticket) => {
                  const ticketDate = new Date(ticket.created_at);
                  return ticketDate > new Date(latest.created_at) ? ticket : latest;
                });

                this.currentYear = new Date(latestTicket.created_at).getFullYear();
                this.currentMonth = new Date(latestTicket.created_at).getMonth();
            }
        })
        .catch((error) => {
            console.error("Błąd podczas pobierania danych:", error);
        });
    },
    // Ustawia wybrany miesiąc
    selectMonth(index) {
      this.currentMonth = index;
    },
    // Ustawia wybrany rok (oraz pierwszy dostępny miesiąc)
    selectYear(year) {
      this.currentYear = year;
      this.currentMonth = this.availableMonths.length > 0 ? this.availableMonths[0].index : 0;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} `
          + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    // różnica w MINUTACH (z ochroną przed wartościami < 0)
    diffMinutes(start, end) {
      if (!start) return 0;
      const from = new Date(start);
      const to = end ? new Date(end) : new Date();
      const ms = to - from;
      return Math.max(0, Math.floor(ms / 60000));
    },
    // "43 min", "1 godz. 20 min", a po 24h: "2 dni 3 godz. 5 min"
    formatDuration(mins) {
      if (mins < 60) return `${mins} min`;

      const hours = Math.floor(mins / 60);
      const minutes = mins % 60;

      // < 24h → godziny i minuty
      if (hours < 24) {
        return minutes ? `${hours} godz. ${minutes} min` : `${hours} godz.`;
      }

      // ≥ 24h → dni, godziny, minuty
      const days = Math.floor(hours / 24);
      const remHours = hours % 24;

      let out = `${days} ${days === 1 ? 'dzień' : 'dni'}`;
      if (remHours) out += ` ${remHours} godz.`;
      if (minutes)  out += ` ${minutes} min`;
      return out;
    },

    // czas trwania jednego zgłoszenia – zwraca gotowy string
    calculateDowntime(createdAt, closeAt) {
      const mins = this.diffMinutes(createdAt, closeAt);
      return this.formatDuration(mins);
    },

    // łączny czas trwania dla listy zgłoszeń – też gotowy string
    totalDowntime(tickets) {
      const totalMins = tickets.reduce(
        (sum, t) => sum + this.diffMinutes(t.created_at, t.closed_at),
        0
      );
      return this.formatDuration(totalMins);
    },
    // Generuje PDF z tabelą ticketów
    generatePDF() {
      if (!this.hasTicketsForSelected) return;

      const doc = new jsPDF();

      doc.text(`Raport za ${this.months[this.currentMonth]} ${this.currentYear}`, 10, 10);

      // Filtrowanie tylko ticketów danej organizacji
      const filteredTickets = this.paginatedTickets.filter(ticket => ticket.organization_id == process.env.ORGANIZATION_ID);

      const tableData = filteredTickets.map((ticket, index) => [
          index + 1,
          ticket.title,
          this.formatDate(ticket.created_at),
          this.formatDate(ticket.closed_at) || 'Niezakonczone',
          this.calculateDowntime(ticket.created_at, ticket.closed_at),
          this.totalDowntime(filteredTickets),
      ]);

      autoTable(doc, {
          head: [['Lp.', 'Opis awarii', 'Data zgloszenia', 'Data usuniecia', 'Czas trwania (godz.)', 'Laczny czas trwania (godz.)', 'Podpis Zamawiajacego', 'Podpis Wykonawcy']],
          body: tableData,
          startY: 20,
      });

      doc.save(`Raport_${this.months[this.currentMonth]}_${this.currentYear}.pdf`);
    },
    goToDetails(id) { 
      this.$router.push({ name: 'ticket-details', params: { id } }); 
    },
    goToEdit(id) { 
      this.$router.push({ name: 'ticket-edit',    params: { id } }); 
    },
  }
};
</script>
<style scoped>
.empty-cell {
  padding: 24px;
  text-align: center;
  color: #666;
  background: #fafafa;
  font-style: italic;
}

.button-save-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.date-picker {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 30px;
}

.year-selector, .month-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.year-selector button, .month-selector button {
  padding: 10px;
  /* border: 2px solid #007e1a; */
  border: 2px solid #0152a1;
  /* border-radius: 8px; */
  background-color: #fff;
  cursor: pointer;
  transition: all 0.3s;
}

.year-selector button.active, .month-selector button.active {
  /* background-color: #007e1a; */
  background-color: #0152a1;
  color: #fff;
  font-weight: bold;
}

.year-selector button:hover, .month-selector button:hover {
  /* background-color: #007e1a; */
  background-color: #0152a1;
  color: #fff;
}

.tickets-table {
  width: 100%;
  border-collapse: collapse;
}

.tickets-table th, .tickets-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: center;
}

.tickets-table th {
  background-color: #ffffff;
  font-weight: bold;
}

.tickets-table tr:nth-child(odd) {
  background-color: #f1f1f1;
}

.tickets-table tr:nth-child(even) {
  background-color: #ffffff;
}

h2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
}

.button-save-outline {
  padding: 0.5rem;
}

.clickable-row { cursor: pointer; }

.button-edit {
  padding: 0.5rem 1rem 0.5rem 1rem;
} 

.button-edit:hover {
  background-color: #C77501 !important;
  color: white !important;
}
</style>